import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../data/site";
import { inputCls, AdminButton } from "../ui";

const GROUPS = [
  {
    page: "Homepage",
    preview: "/",
    fields: [
      { key: "home_hero_eyebrow", label: "Hero Eyebrow (small text above headline)" },
      { key: "home_hero_line1", label: "Main Headline — Line 1" },
      { key: "home_hero_line2", label: "Main Headline — Line 2" },
      { key: "home_hero_sub", label: "Subheadline", multiline: true },
      { key: "home_intro_headline", label: "Intro Headline" },
      { key: "home_intro_p1", label: "Intro Text — Paragraph 1", multiline: true },
      { key: "home_intro_p2", label: "Intro Text — Paragraph 2", multiline: true },
      { key: "home_custom_headline1", label: "Custom Section Headline — Line 1" },
      { key: "home_custom_headline2", label: "Custom Section Headline — Line 2" },
      { key: "home_custom_headline3", label: "Custom Section Headline — Line 3" },
      { key: "home_custom_sub", label: "Custom Section Text", multiline: true },
      { key: "home_cta_headline1", label: "Final CTA Headline — Line 1" },
      { key: "home_cta_headline2", label: "Final CTA Headline — Line 2" },
      { key: "home_cta_button", label: "CTA Button Text" },
    ],
  },
  {
    page: "Custom Homes",
    preview: "/custom-homes",
    fields: [
      { key: "custom_headline1", label: "Page Headline — Line 1" },
      { key: "custom_headline2", label: "Page Headline — Line 2" },
      { key: "custom_intro", label: "Intro Text", multiline: true },
      { key: "custom_plans_headline1", label: "Floor Plans Headline — Line 1" },
      { key: "custom_plans_headline2", label: "Floor Plans Headline — Line 2" },
      { key: "custom_plans_intro", label: "Floor Plans Intro", multiline: true },
      { key: "custom_existing_title", label: "Existing Plans — Title" },
      { key: "custom_existing_text", label: "Existing Plans — Text", multiline: true },
      { key: "custom_full_title", label: "Fully Custom — Title" },
      { key: "custom_full_text", label: "Fully Custom — Text", multiline: true },
      { key: "custom_cta_headline", label: "CTA Headline" },
      { key: "custom_cta_text", label: "CTA Text", multiline: true },
      { key: "custom_cta_button", label: "CTA Button Text" },
    ],
  },
  {
    page: "About / Meet Mark",
    preview: "/about",
    fields: [
      { key: "about_headline", label: "Headline" },
      { key: "about_bio", label: "Bio (blank line between paragraphs)", multiline: true, rows: 12 },
    ],
  },
  {
    page: "Where We Build",
    preview: "/",
    fields: [
      { key: "areas_headline", label: "Headline" },
      { key: "areas_text", label: "Description", multiline: true },
    ],
  },
  {
    page: "Contact",
    preview: "/contact",
    fields: [
      { key: "contact_headline1", label: "Headline — Line 1" },
      { key: "contact_headline2", label: "Headline — Line 2" },
      { key: "contact_intro", label: "Description", multiline: true },
      { key: "contact_cta_button", label: "Submit Button Text" },
    ],
  },
];

const ContentPanel = () => {
  const { data, refetch } = useQuery({
    queryKey: ["content-admin"],
    queryFn: async () => (await axios.get(`${API}/content-admin`, { withCredentials: true })).data,
  });
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState({});

  useEffect(() => {
    if (data) {
      const d = {}, p = {};
      data.forEach((i) => { d[i.key] = i.draft; p[i.key] = i.published; });
      setDrafts(d);
      setPublished(p);
    }
  }, [data]);

  const dirtyKeys = Object.keys(drafts).filter((k) => drafts[k] !== published[k]);

  const saveDrafts = async () => {
    setSaving(true);
    try {
      await Promise.all(dirtyKeys.map((k) => axios.put(`${API}/content`, { key: k, value: drafts[k] }, { withCredentials: true })));
      toast.success(`${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"} saved as draft — not live yet`);
      refetch();
    } finally {
      setSaving(false);
    }
  };

  const publishAll = async () => {
    if (!window.confirm(`Publish ${dirtyKeys.length} pending change${dirtyKeys.length === 1 ? "" : "s"} to the live website?`)) return;
    setSaving(true);
    try {
      await Promise.all(dirtyKeys.map((k) => axios.put(`${API}/content`, { key: k, value: drafts[k] }, { withCredentials: true })));
      await axios.post(`${API}/content/publish`, { keys: dirtyKeys }, { withCredentials: true });
      toast.success("Published — changes are now live on the website");
      refetch();
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#A3A09B]">Loading…</p>;

  return (
    <div data-testid="content-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 sticky top-0 bg-[#F7F5F2] py-4 z-10 border-b border-[rgba(26,26,26,0.1)]">
        <p className="text-sm text-[#5C5A56]">
          {dirtyKeys.length > 0 ? (
            <span className="text-[#A37C45] font-semibold" data-testid="content-dirty-count">{dirtyKeys.length} unpublished change{dirtyKeys.length === 1 ? "" : "s"}</span>
          ) : (
            <span className="text-[#4E735B] font-semibold" data-testid="content-clean">Everything is published</span>
          )}
        </p>
        <div className="flex gap-3">
          <AdminButton onClick={saveDrafts} disabled={saving || dirtyKeys.length === 0} testid="content-save-draft">Save Draft</AdminButton>
          <AdminButton primary type="button" onClick={publishAll} disabled={saving || dirtyKeys.length === 0} testid="content-publish">Publish to Website</AdminButton>
        </div>
      </div>

      {GROUPS.map((g) => (
        <div key={g.page} className="mb-12">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl tracking-tight">{g.page}</h2>
            <a href={g.preview} target="_blank" rel="noreferrer" className="font-mono text-[0.62rem] tracking-[0.15em] uppercase font-bold text-[#8C6A4F] link-underline" data-testid={`content-preview-${g.page.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              Preview Page ↗
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {g.fields.map((f) => (
              <label key={f.key} className={`block ${f.multiline ? "md:col-span-2" : ""}`} data-testid={`content-field-${f.key}`}>
                <span className="eyebrow text-[#5C5A56] mb-1 flex items-center gap-2">
                  {f.label}
                  {drafts[f.key] !== published[f.key] && <span className="text-[#A37C45] normal-case tracking-normal">• edited, not published</span>}
                </span>
                {f.multiline ? (
                  <textarea rows={f.rows || 3} value={drafts[f.key] || ""} onChange={(e) => setDrafts({ ...drafts, [f.key]: e.target.value })} className={inputCls} data-testid={`content-input-${f.key}`} />
                ) : (
                  <input value={drafts[f.key] || ""} onChange={(e) => setDrafts({ ...drafts, [f.key]: e.target.value })} className={inputCls} data-testid={`content-input-${f.key}`} />
                )}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ContentPanel;
